import { Component, Input, ElementRef, Self, Optional, OnDestroy, DoCheck } from '@angular/core';
import { ControlValueAccessor, UntypedFormGroup, UntypedFormControl, NgControl } from '@angular/forms';
import { MatFormFieldControl } from '@angular/material/form-field';
import { Subject } from 'rxjs';
import { FocusMonitor } from '@angular/cdk/a11y';

export interface ExpirationDate {
    month?: number;
    year?: number;
}

@Component({
    selector: 'app-expiration-date-input',
    templateUrl: './expiration-date-input.component.html',
    styleUrls: ['./expiration-date-input.component.scss'],
    providers: [
        { provide: MatFormFieldControl, useExisting: ExpirationDateInputComponent }
    ],
    // tslint:disable-next-line: no-host-metadata-property
    host: {
        '[class.floating]': 'shouldLabelFloat',
        '[id]': 'id',
        '[attr.aria-describedby]': 'describedBy',
    }
})
export class ExpirationDateInputComponent implements ControlValueAccessor, MatFormFieldControl<ExpirationDate>, OnDestroy, DoCheck {

    get empty() {
        const {value: { month, year }} = this.formGroup;
        return !month && !year;
    }

    get shouldLabelFloat() { return this.focused || !this.empty; }

    @Input()
    get placeholder(): string { return this.placeholderValue; }
    set placeholder(value: string) {
        this.placeholderValue = value;
        this.stateChanges.next();
    }

    @Input()
    get required(): boolean { return this.requiredValue; }
    set required(value: boolean) {
        this.requiredValue = value;
        this.stateChanges.next();
    }

    @Input()
    get value(): ExpirationDate | null {
        if (this.formGroup.valid && !this.empty) {
            const { value } = this.formGroup;
            return value;
        }
        return null;
    }
    set value(expirationDate: ExpirationDate | null) {
        const value = expirationDate || { month: '', year: '' };
        this.formGroup.setValue(value);
        this.stateChanges.next();
    }

    @Input()
    get disabled(): boolean { return this.disabledValue; }
    set disabled(value: boolean) {
        this.disabledValue = value;
        this.disabledValue ? this.formGroup.disable() : this.formGroup.enable();
        this.stateChanges.next();
    }

    constructor(
        private focusMonitor: FocusMonitor,
        private elementRef: ElementRef<HTMLElement>,
        @Optional() @Self() public ngControl: NgControl) {

        focusMonitor.monitor(elementRef, true).subscribe(origin => {
            if (this.focused && !origin) {
                this.onTouched();
            }
            this.focused = !!origin;
            this.stateChanges.next();
        });

        if (this.ngControl != null) {
            this.ngControl.valueAccessor = this;
        }
    }

    // tslint:disable-next-line: variable-name
    static ngAcceptInputType_disabled: boolean | string | null | undefined;
    // tslint:disable-next-line: variable-name
    static ngAcceptInputType_required: boolean | string | null | undefined;

    formGroup = new UntypedFormGroup({
        month: new UntypedFormControl('', []),
        year: new UntypedFormControl('', []),
    });
    stateChanges = new Subject<void>();
    focused = false;
    errorState = false;
    controlType = 'app-expiration-date-input';
    describedBy = '';

    @Input()
    id: string;
    private placeholderValue = '';
    private requiredValue = false;
    private disabledValue = false;
    onChange = (_: any) => {};
    onTouched = () => {};

    ngOnDestroy() {
        this.stateChanges.complete();
        this.focusMonitor.stopMonitoring(this.elementRef);
    }

    setDescribedByIds(ids: string[]): void {
        this.describedBy = ids.join(' ');
    }

    onContainerClick(event: MouseEvent): void {
        if ((event.target as Element).tagName.toLowerCase() !== 'input') {
            this.elementRef.nativeElement.querySelector('input')?.focus();
        }
    }

    writeValue(obj: ExpirationDate) {
        if (obj !== null && obj !== undefined) {
            this.value = obj;
        }
    }

    registerOnChange(fn: (p: ExpirationDate) => void) {
        this.onChange = fn;
    }

    registerOnTouched(fn: any) {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean) {
        this.disabled = isDisabled;
    }

    ngDoCheck() {
        if (this.ngControl) {
            this.errorState = this.ngControl.invalid && this.ngControl.touched;
            this.stateChanges.next();
        }
    }
}
